import { Modal } from "carbon-components-react";
import * as moment from "moment";
import React, { useState, useEffect } from "react";

import APIClient from "web_modules/APIClient";

import { Indexes } from "@eave/shared/types/api";
import {
  decimalToFivePlaces,
  decimalToThreePlaces,
  timestampToLastUpdatedStr,
} from "web_modules/capital_markets_dashboard/shared/utils";
import HeaderButton from "web_modules/capital_markets_dashboard/shared/HeaderButton";

import { IndexResponse } from "../interfaces";

const styles = require("./styles.module.scss");

const IndexURL = "/cm/index";

const GetLatestIndex = (indexName: Indexes) =>
  APIClient.get(`${IndexURL}?name=${indexName}`) as Promise<{ index: IndexResponse }>;

const PostLatestIndex = (newIndex: string, indexName: Indexes) =>
  APIClient.post(IndexURL, { value: newIndex, name: indexName }) as Promise<{
    index: IndexResponse;
  }>;

const validationErrors = (newIndex: string, indexName: Indexes): string[] => {
  if (newIndex.trim() === "" && newIndex !== "") {
    return [`${indexName} rate cannot be empty`];
  }

  const indexAsNum = +newIndex;
  if (isNaN(indexAsNum)) {
    return [`${indexName} rate must be a number: ${newIndex}`];
  }

  const errorMessages = [];

  const numSplit = newIndex.split(".");
  if (numSplit.length === 2 && numSplit[1].length > 5) {
    errorMessages.push(`${newIndex} rate only supports up to 5 decimal places`);
  }
  if (indexAsNum < 0) {
    errorMessages.push(`${newIndex} rate cannot be negative`);
  }

  return errorMessages;
};

interface Props {
  indexName: Indexes;
  indexLabel: string;
  linkTitle: string;
  linkHref: string;
  updateLastUpdated: Function; // TODO: jjw: can remove when using provider
}

const IndexEditor: React.FC<Props> = ({
  indexName,
  indexLabel,
  linkTitle,
  linkHref,
  updateLastUpdated,
}) => {
  const [prevIndex, setPrevIndex] = useState<string>("");
  const [prevRoundedIndex, setPrevRoundedIndex] = useState<string>("");
  const [newIndex, setNewIndex] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [updateAgeDays, setUpdateAgeDays] = useState<number>(0);

  useEffect(() => {
    GetLatestIndex(indexName).then(({ index: { value, rounded_value, created_at } }) => {
      setPrevIndex(decimalToFivePlaces(value));
      setPrevRoundedIndex(decimalToThreePlaces(rounded_value));
      setLastUpdated(created_at);
      setUpdateAgeDays(moment().diff(created_at, "days"));
    });
  }, [indexName]);

  useEffect(() => {
    if (!prevIndex && !newIndex) return;
    const errors = validationErrors(newIndex, indexName);
    if (errors.length !== 0) {
      setErrorMessage(errors.join(" and "));
    } else {
      setErrorMessage("");
    }
  }, [indexName, newIndex, prevIndex]);

  const handleOnSave = async () => {
    try {
      const {
        index: { value, rounded_value },
      } = await PostLatestIndex(newIndex, indexName);
      setPrevIndex(decimalToFivePlaces(value));
      setPrevRoundedIndex(decimalToThreePlaces(rounded_value));

      // TODO: jjw: can remove when using provider
      updateLastUpdated(moment());
    } catch {
      alert(`there was an error updating the ${indexName} rate, please contact engineering`);
      throw new Error(`there was an error updating the ${indexName} rate to ${newIndex}`);
    }
    setNewIndex("");
  };

  const renderIndexWarningModal = (): React.ReactNode => {
    if (updateAgeDays < 7) {
      // all good, index is recent, or already hid warning
      return null;
    }

    return (
      <Modal
        open
        passiveModal
        modalHeading={`Stale ${indexName} Index`}
        onRequestClose={() => setUpdateAgeDays(0)}
      >
        {`${indexName} must be updated. It was last updated ${updateAgeDays} days ago, on ${moment
          .utc(lastUpdated)
          .format("MMMM Do YYYY")}.`}
      </Modal>
    );
  };

  return (
    <div className={styles.container}>
      {renderIndexWarningModal()}
      <div className={styles.formContainerGrid}>
        <h3 className={styles.currentLabelItem}>{indexLabel}</h3>
        <span className={styles.timestampLabel}>{timestampToLastUpdatedStr(lastUpdated)}</span>
        <input
          type="text"
          className={styles.currentInput}
          value={`${prevIndex}%`}
          disabled
          readOnly
        />
        <h3 className={styles.roundedLabelItem}>{`Current truncated ${indexName}`}</h3>
        <input
          type="text"
          className={styles.roundedInput}
          value={`${prevRoundedIndex}%`}
          disabled
          readOnly
        />
        <h3 className={styles.newLabelItem}>{`New ${indexName}`}</h3>
        <input
          type="text"
          className={styles.newInput}
          name="Index"
          value={newIndex}
          placeholder={`Enter latest ${indexName}`}
          onChange={e => setNewIndex(e.target.value)}
        />
        <a className={styles.linkItem} href={linkHref} target="_blank">
          {linkTitle}
        </a>
        {errorMessage && <span className={styles.errMsgItem}>{errorMessage}</span>}
      </div>
      <div>
        <HeaderButton onClick={handleOnSave} disabled={errorMessage !== "" || newIndex === ""}>
          {`Save ${indexName} changes`}
        </HeaderButton>
      </div>
    </div>
  );
};

export default IndexEditor;
